import { BigNum } from '@velocity-exchange/sdk';
import {
	ENUM_UTILS,
	UIMatchedOrderRecordAndAction,
	UISerializableOrderActionRecord,
	UISerializableOrderRecord,
	getVelocityEventKey,
	sortUIOrderActionRecords,
} from '@velocity-exchange/common';
import type { ParentOrderInfo } from 'src/@types/historyTables';
import { ZERO_QUOTE_BIGNUM } from 'src/constants/math';
import { getOrderStatusString } from 'src/utils/order';

/**
 *
 * Need unmatched OrderRecords
 * Unmatched ActionRecords
 * Matching-Key generation for ORder Records + Action Records
 * Matched Action Records lookup < - > Action Record Key generation
 *
 */

class OrderRecordAndActionStateHandler {
	private static readonly MAX_ORDER_RECORD_CACHE_SIZE = 500;
	private static readonly MAX_MATCHED_ACTION_CACHE_SIZE = 1000;
	private static readonly MAX_UNMATCHED_ACTIONS = 100;

	orderRecordCache: Map<string, UISerializableOrderRecord> = new Map();
	matchedActionRecordCache: Map<string, UISerializableOrderActionRecord> =
		new Map();
	unmatchedActions: UISerializableOrderActionRecord[] = [];

	private evictOldestFromMap<K, V>(map: Map<K, V>, maxSize: number): void {
		if (map.size <= maxSize) return;

		const numToDelete = map.size - maxSize;
		const keysToDelete = Array.from(map.keys()).slice(0, numToDelete);

		keysToDelete.forEach((key) => map.delete(key));
	}

	private getOrderRecordMatchingKey = (record: UISerializableOrderRecord) => {
		return `_${record.user.toString()}_${ENUM_UTILS.toStr(
			record.order.marketType
		)}_${record.order.marketIndex.toString()}_${record.order.orderId.toString()}`;
	};
	private getTakerActionMatchingKey = (
		record: UISerializableOrderActionRecord
	) => {
		return `_${record.taker?.toString() ?? ''}_${ENUM_UTILS.toStr(
			record.marketType
		)}_${record.marketIndex.toString()}_${
			record.takerOrderId?.toString() ?? ''
		}`;
	};
	private getMakerActionMatchingKey = (
		record: UISerializableOrderActionRecord
	) => {
		return `_${record.maker?.toString() ?? ''}_${ENUM_UTILS.toStr(
			record.marketType
		)}_${record.marketIndex.toString()}_${
			record.makerOrderId?.toString() ?? ''
		}`;
	};

	private getTakerActionUniquenessKey = (
		record: UISerializableOrderActionRecord
	) => {
		return `_${record.taker?.toString() ?? ''}_${ENUM_UTILS.toStr(
			record.marketType
		)}_${record.marketIndex.toString()}_${
			record.takerOrderId?.toString() ?? ''
		}_$${record.fillRecordId}_${ENUM_UTILS.toStr(
			record.action
		)}_${record.txSig.toString()}`;
	};

	private getMakerActionUniquenessKey = (
		record: UISerializableOrderActionRecord
	) => {
		return `_${record.maker?.toString() ?? ''}_${ENUM_UTILS.toStr(
			record.marketType
		)}_${record.marketIndex.toString()}_${
			record.makerOrderId?.toString() ?? ''
		}_$${record.fillRecordId}_${ENUM_UTILS.toStr(
			record.action
		)}_${record.txSig.toString()}`;
	};

	private actionAlreadyMatched = (
		actionRecord: UISerializableOrderActionRecord
	) => {
		if (actionRecord.taker) {
			const takerMatchingKey = this.getTakerActionUniquenessKey(actionRecord);
			if (this.matchedActionRecordCache.get(takerMatchingKey)) return true;
		}

		if (actionRecord.maker) {
			const makerMatchingKey = this.getMakerActionUniquenessKey(actionRecord);
			if (this.matchedActionRecordCache.get(makerMatchingKey)) return true;
		}

		return false;
	};

	private markActionsAsMatched(
		actionRecords: UISerializableOrderActionRecord[]
	) {
		const unmatched = actionRecords.filter(
			(action) => !this.actionAlreadyMatched(action)
		);
		unmatched.forEach((action) => this.addMatchedActionRecordToCache(action));
	}

	private actionMatchesRecordCacheId = (
		action: UISerializableOrderActionRecord,
		recordCacheId: string
	) => {
		if (action.maker) {
			if (this.getMakerActionMatchingKey(action) === recordCacheId) return true;
		}

		if (action.taker) {
			if (this.getTakerActionMatchingKey(action) === recordCacheId) return true;
		}

		return false;
	};

	private addMatchedActionRecordToCache = (
		unmatchedAction: UISerializableOrderActionRecord
	) => {
		if (unmatchedAction.maker) {
			this.matchedActionRecordCache.set(
				this.getMakerActionUniquenessKey(unmatchedAction),
				unmatchedAction
			);
		}

		if (unmatchedAction.taker) {
			this.matchedActionRecordCache.set(
				this.getTakerActionUniquenessKey(unmatchedAction),
				unmatchedAction
			);
		}

		this.evictOldestFromMap(
			this.matchedActionRecordCache,
			OrderRecordAndActionStateHandler.MAX_MATCHED_ACTION_CACHE_SIZE
		);
	};

	public static getOrderUniquenessKey = (
		record: UISerializableOrderActionRecord
	) => {
		return getVelocityEventKey(record);
	};

	public ordersAreIdentical = (
		recordA: UISerializableOrderActionRecord,
		recordB: UISerializableOrderActionRecord
	) => {
		return (
			OrderRecordAndActionStateHandler.getOrderUniquenessKey(recordA) ===
			OrderRecordAndActionStateHandler.getOrderUniquenessKey(recordB)
		);
	};

	public static getSortedDedupedTrimmedRecords = (
		records: UISerializableOrderActionRecord[],
		cap?: number
	) => {
		if (cap == undefined) cap = records.length;

		const newRecordsMap = new Map<string, UISerializableOrderActionRecord>();
		for (const r of records) {
			newRecordsMap.set(this.getOrderUniquenessKey(r), r);
		}

		return sortUIOrderActionRecords([...newRecordsMap.values()]).slice(
			0,
			cap
		) as UISerializableOrderActionRecord[];
	};

	public addOrderRecord(record: UISerializableOrderRecord) {
		const cacheId = this.getOrderRecordMatchingKey(record);

		const newlyMatchedActions = this.unmatchedActions.filter((action) => {
			return this.actionMatchesRecordCacheId(action, cacheId);
		});

		// Clear newly matched actions from unmatched list
		if (newlyMatchedActions.length > 0) {
			this.unmatchedActions = this.unmatchedActions.filter((action) => {
				return !this.actionMatchesRecordCacheId(action, cacheId);
			});
		}

		// Mark newly matched actions in cache
		this.markActionsAsMatched(newlyMatchedActions);

		this.orderRecordCache.set(cacheId, record);

		this.evictOldestFromMap(
			this.orderRecordCache,
			OrderRecordAndActionStateHandler.MAX_ORDER_RECORD_CACHE_SIZE
		);
	}

	public addActionRecord(newAction: UISerializableOrderActionRecord) {
		if (this.actionAlreadyMatched(newAction)) {
			return;
		}

		const unmatchedActionsToCheck = [...this.unmatchedActions, newAction];
		const newUnmatchedActions: UISerializableOrderActionRecord[] = [];

		// For each currently unmatched action, check if it matches new order state and removed from unmatched state if so
		unmatchedActionsToCheck.forEach((unmatchedAction) => {
			let foundMatch = false;

			if (unmatchedAction.maker) {
				const makerMatchingKey =
					this.getMakerActionMatchingKey(unmatchedAction);

				const matchingOrderRecord = this.orderRecordCache.get(makerMatchingKey);

				if (matchingOrderRecord) {
					foundMatch = true;
				}
			}

			if (unmatchedAction.taker) {
				const takerMatchingKey =
					this.getTakerActionMatchingKey(unmatchedAction);

				const matchingOrderRecord = this.orderRecordCache.get(takerMatchingKey);

				if (matchingOrderRecord) {
					foundMatch = true;
				}
			}

			if (foundMatch) {
				this.markActionsAsMatched([unmatchedAction]);
			} else {
				newUnmatchedActions.push(unmatchedAction);
			}
		});

		this.unmatchedActions = newUnmatchedActions.slice(
			-OrderRecordAndActionStateHandler.MAX_UNMATCHED_ACTIONS
		);
	}

	public static convertLegacyOrderRecordToParentOrderInfo = (
		matchedOrderRecord: UIMatchedOrderRecordAndAction
	): ParentOrderInfo => {
		const isTaker = matchedOrderRecord.actionRecord.taker?.equals(
			matchedOrderRecord.orderRecord.user
		);
		const quoteAssetAmountFilled = isTaker
			? (matchedOrderRecord.actionRecord
					.takerOrderCumulativeQuoteAssetAmountFilled ?? BigNum.zero())
			: (matchedOrderRecord.actionRecord
					.makerOrderCumulativeQuoteAssetAmountFilled ?? BigNum.zero());
		const baseAssetAmountFilled = isTaker
			? (matchedOrderRecord.actionRecord
					.takerOrderCumulativeBaseAssetAmountFilled ?? BigNum.zero())
			: (matchedOrderRecord.actionRecord
					.makerOrderCumulativeBaseAssetAmountFilled ?? BigNum.zero());
		const orderInfo: ParentOrderInfo = {
			marketType: matchedOrderRecord.orderRecord.order.marketType,
			orderId: matchedOrderRecord.orderRecord.order.orderId,
			orderType: matchedOrderRecord.orderRecord.order.orderType,
			triggerPrice: matchedOrderRecord.orderRecord.order.triggerPrice,
			price: matchedOrderRecord.orderRecord.order.price,
			oraclePriceOffset: matchedOrderRecord.orderRecord.order.oraclePriceOffset,
			reduceOnly: matchedOrderRecord.orderRecord.order.reduceOnly,
			postOnly: matchedOrderRecord.orderRecord.order.postOnly,
			immediateOrCancel: matchedOrderRecord.orderRecord.order.immediateOrCancel,
			direction: matchedOrderRecord.orderRecord.order.direction,
			triggerCondition: matchedOrderRecord.orderRecord.order.triggerCondition,
			existingPositionDirection:
				matchedOrderRecord.orderRecord.order.existingPositionDirection,
			ts: matchedOrderRecord.orderRecord.ts,
			marketIndex: matchedOrderRecord.orderRecord.order.marketIndex,
			baseAssetAmountFilled: baseAssetAmountFilled,
			quoteAssetAmountFilled: quoteAssetAmountFilled,
			baseAssetAmount: matchedOrderRecord.orderRecord.order.baseAssetAmount,
			slot: matchedOrderRecord.orderRecord.order.slot.toNumber(),
			lastActionStatus: getOrderStatusString(
				matchedOrderRecord.actionRecord.action,
				baseAssetAmountFilled ?? BigNum.zero(),
				matchedOrderRecord.orderRecord.order.baseAssetAmount
			),
			lastUpdatedTs: matchedOrderRecord.actionRecord.ts,
			lastActionExplanation: matchedOrderRecord.actionRecord.actionExplanation,
			cumulativeFee: ZERO_QUOTE_BIGNUM,
		};

		return orderInfo;
	};

	public sortParentOrdersInfo = (
		orders: ParentOrderInfo[],
		direction: 'asc' | 'desc' = 'desc'
	) => {
		return [...orders].sort((a, b) => {
			return direction === 'asc'
				? a.ts.toNumber() - b.ts.toNumber()
				: b.ts.toNumber() - a.ts.toNumber();
		});
	};
}

export default OrderRecordAndActionStateHandler;
